import {
  CheckCircle2,
  Download,
  Eye,
  FileCheck2,
  PackageOpen,
  RotateCcw,
  TriangleAlert,
} from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import type { Locale } from '../../../domain/tools/types';
import { formatBytes } from '../../../lib/format';
import '../workspace.css';

export interface WorkspaceResultArtifact {
  readonly id: string;
  readonly name: string;
  readonly size: number;
  readonly mimeType?: string;
  readonly detail?: string;
}

export type WorkspaceMetricTone = 'neutral' | 'positive' | 'attention';

export interface WorkspaceResultMetric {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly tone?: WorkspaceMetricTone;
}

export interface ResultPanelProps {
  readonly artifacts: readonly WorkspaceResultArtifact[];
  readonly metrics?: readonly WorkspaceResultMetric[];
  readonly warnings?: readonly string[];
  readonly title?: string;
  readonly validationPassed?: boolean;
  readonly validationMessage?: string;
  readonly busy?: boolean;
  readonly onDownload: (artifactId: string) => void;
  readonly onDownloadAll?: () => void;
  readonly onPreview?: (artifactId: string) => void;
  readonly onStartOver?: () => void;
}

function localeFromLanguage(language: string): Locale {
  return language.toLowerCase().startsWith('ar') ? 'ar' : 'en';
}

export function ResultPanel({
  artifacts,
  metrics = [],
  warnings = [],
  title,
  validationPassed,
  validationMessage,
  busy = false,
  onDownload,
  onDownloadAll,
  onPreview,
  onStartOver,
}: ResultPanelProps) {
  const { t, i18n } = useTranslation();
  const locale = localeFromLanguage(i18n.resolvedLanguage ?? i18n.language);
  const titleId = useId();
  const resolvedTitle = title ?? (
    artifacts.length === 1 ? t('workflow.result.title') : t('workflow.result.multipleTitle')
  );

  return (
    <section className="workspace-result" aria-labelledby={titleId} aria-busy={busy}>
      <div className="workspace-result__hero">
        <span className="workspace-result__success" data-valid={validationPassed} aria-hidden="true">
          {validationPassed === false ? <TriangleAlert size={27} /> : <CheckCircle2 size={28} />}
        </span>
        <div>
          <h2 id={titleId}>{resolvedTitle}</h2>
          {validationPassed !== undefined && (
            <p className="workspace-result__validation" data-valid={validationPassed}>
              {validationPassed ? <FileCheck2 size={16} aria-hidden="true" /> : <TriangleAlert size={16} aria-hidden="true" />}
              {validationMessage ?? (
                validationPassed ? t('workflow.result.validated') : t('workflow.result.validationWarning')
              )}
            </p>
          )}
        </div>
      </div>

      {metrics.length > 0 && (
        <dl className="workspace-result__metrics">
          {metrics.map((metric) => (
            <div key={metric.id} data-tone={metric.tone ?? 'neutral'}>
              <dt>{metric.label}</dt>
              <dd>{metric.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {warnings.length > 0 && (
        <ul className="workspace-messages" aria-live="polite">
          {warnings.map((warning, index) => (
            <li key={index} data-tone="warning">
              <TriangleAlert size={16} aria-hidden="true" />
              {warning}
            </li>
          ))}
        </ul>
      )}

      <ul className="workspace-result__files">
        {artifacts.map((artifact) => (
          <li key={artifact.id}>
            <span className="workspace-result__file-icon" aria-hidden="true">
              <PackageOpen size={20} />
            </span>
            <span className="workspace-result__file-body">
              <bdi dir="auto">{artifact.name}</bdi>
              <small>
                {formatBytes(artifact.size, locale)}
                {artifact.mimeType && <> · <bdi dir="ltr">{artifact.mimeType}</bdi></>}
                {artifact.detail && <> · {artifact.detail}</>}
              </small>
            </span>
            <span className="workspace-result__file-actions">
              {onPreview && (
                <button
                  type="button"
                  disabled={busy}
                  aria-label={`${t('workflow.result.previewResult')}: ${artifact.name}`}
                  onClick={() => onPreview(artifact.id)}
                >
                  <Eye size={18} aria-hidden="true" />
                </button>
              )}
              <button
                type="button"
                disabled={busy}
                aria-label={t('workflow.result.downloadNamed', { name: artifact.name })}
                onClick={() => onDownload(artifact.id)}
              >
                <Download size={18} aria-hidden="true" />
              </button>
            </span>
          </li>
        ))}
      </ul>

      <div className="workspace-result__actions">
        {artifacts.length > 1 && onDownloadAll && (
          <button className="workspace-primary-button" type="button" disabled={busy} onClick={onDownloadAll}>
            <Download size={18} aria-hidden="true" />
            {t('common.actions.downloadZip')}
          </button>
        )}
        {artifacts.length === 1 && artifacts[0] && (
          <button
            className="workspace-primary-button"
            type="button"
            disabled={busy}
            onClick={() => onDownload(artifacts[0]!.id)}
          >
            <Download size={18} aria-hidden="true" />
            {t('workflow.result.downloadResult')}
          </button>
        )}
        {onStartOver && (
          <button className="workspace-secondary-button" type="button" disabled={busy} onClick={onStartOver}>
            <RotateCcw size={17} aria-hidden="true" />
            {t('common.actions.startOver')}
          </button>
        )}
      </div>
    </section>
  );
}
